import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Button,
  CircularProgress,
  Alert,
  Divider,
  FormControlLabel,
  Switch,
  Chip,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Publish as PublishIcon,
  Image as ImageIcon,
  Link as LinkIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cmsService, CMSBlogPost, CMSBlogCategory, CMSBlogAuthor, CMSBlogPostInput } from '../../../services';

const PostEditor: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  // A rota «blog/posts/new» é literal e não traz :id — sem id é artigo novo.
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [categories, setCategories] = useState<CMSBlogCategory[]>([]);
  const [authors, setAuthors] = useState<CMSBlogAuthor[]>([]);

  const [formData, setFormData] = useState<CMSBlogPostInput>({
    slug: '',
    titulo: '',
    resumo: '',
    conteudo: '',
    categoria_id: undefined,
    autor_id: undefined,
    imagem_destaque: '',
    tempo_leitura: 5,
    destaque: false,
    tags: '',
    meta_descricao: '',
    status: 'rascunho',
    data_publicacao: undefined,
  });

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const fetchPost = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const response = await cmsService.getBlogPost(parseInt(id!));
      const post = response.data.data;
      setFormData({
        slug: post.slug,
        titulo: post.titulo,
        resumo: post.resumo || '',
        conteudo: post.conteudo || '',
        categoria_id: post.categoria_id,
        autor_id: post.autor_id,
        imagem_destaque: post.imagem_destaque || '',
        tempo_leitura: post.tempo_leitura || 5,
        destaque: post.destaque || false,
        tags: post.tags || '',
        meta_descricao: post.meta_descricao || '',
        status: post.status,
        data_publicacao: post.data_publicacao,
      });
    } catch (err: any) {
      setError(err.message || t('cms.errorLoadingPost'));
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  const fetchOptions = useCallback(async () => {
    try {
      const [catRes, authRes] = await Promise.all([
        cmsService.getBlogCategories(),
        cmsService.getBlogAuthors(),
      ]);
      setCategories(catRes.data.data || []);
      setAuthors(authRes.data.data || []);
    } catch (err) {
      console.error('Error fetching options:', err);
    }
  }, []);

  useEffect(() => {
    fetchOptions();
    fetchPost();
  }, [fetchOptions, fetchPost]);

  const handleChange = (field: keyof CMSBlogPostInput) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Auto-generate slug from title
    if (field === 'titulo' && isNew) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(value),
      }));
    }
  };

  const handleSelectChange = (field: keyof CMSBlogPostInput) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value === '' ? undefined : (field === 'categoria_id' || field === 'autor_id' || field === 'tempo_leitura' ? parseInt(value) : value),
    }));
  };

  const handleSwitchChange = (field: keyof CMSBlogPostInput) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleSubmit = async (publish: boolean = false) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const dataToSend = {
        ...formData,
        status: publish ? 'publicado' as const : formData.status,
        // data_publicacao é hora de parede de CV (o site compara com «agora em CV»):
        // publicar sem data envia a hora actual de CV, não a UTC.
        data_publicacao: publish && !formData.data_publicacao
          ? new Date(Date.now() - 3_600_000).toISOString()
          : formData.data_publicacao,
      };

      if (isNew) {
        await cmsService.createBlogPost(dataToSend);
        setSuccess(t('cms.postCreated'));
        setTimeout(() => navigate('/cms/blog/posts'), 1500);
      } else {
        await cmsService.updateBlogPost(parseInt(id!), dataToSend);
        setSuccess(t('cms.postUpdated'));
        if (publish) {
          await cmsService.publishBlogPost(parseInt(id!));
        }
      }
    } catch (err: any) {
      setError(err.message || t('cms.errorSavingPost'));
    } finally {
      setSaving(false);
    }
  };

  const estimateReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const words = content.trim().split(/\s+/).length;
    return Math.max(1, Math.ceil(words / wordsPerMinute));
  };

  useEffect(() => {
    if (formData.conteudo) {
      const estimated = estimateReadTime(formData.conteudo);
      if (estimated !== formData.tempo_leitura) {
        setFormData(prev => ({ ...prev, tempo_leitura: estimated }));
      }
    }
  }, [formData.conteudo]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/cms/blog/posts')}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {isNew ? t('cms.newPostTitle') : t('cms.editPostTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isNew ? t('cms.newPostSubtitle') : t('cms.editingPost', { title: formData.titulo })}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={() => handleSubmit(false)}
            disabled={saving}
          >
            {saving ? t('common.saving') : t('cms.saveDraft')}
          </Button>
          <Button
            variant="contained"
            startIcon={<PublishIcon />}
            onClick={() => handleSubmit(true)}
            disabled={saving}
          >
            {saving ? t('cms.publishing') : t('cms.publish')}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('cms.contentSection')}
              </Typography>

              <TextField
                fullWidth
                label={t('cms.titleLabel')}
                value={formData.titulo}
                onChange={handleChange('titulo')}
                margin="normal"
                required
                placeholder={t('cms.titlePostPlaceholder')}
              />

              <TextField
                fullWidth
                label={t('cms.slugArticle')}
                value={formData.slug}
                onChange={handleChange('slug')}
                margin="normal"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon fontSize="small" />
                      /blog/
                    </InputAdornment>
                  ),
                }}
                helperText={t('cms.slugArticleHelperText')}
              />

              <TextField
                fullWidth
                label={t('cms.summary')}
                value={formData.resumo}
                onChange={handleChange('resumo')}
                margin="normal"
                multiline
                rows={3}
                placeholder={t('cms.summaryPlaceholder')}
                helperText={t('cms.summaryHelperText')}
              />

              <TextField
                fullWidth
                label={t('cms.contentLabel')}
                value={formData.conteudo}
                onChange={handleChange('conteudo')}
                margin="normal"
                multiline
                rows={20}
                placeholder={t('cms.contentPostPlaceholder')}
                helperText={t('cms.readTimeEstimate', { min: formData.tempo_leitura })}
              />
            </CardContent>
          </Card>

          {/* SEO */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('cms.seoMeta')}
              </Typography>

              <TextField
                fullWidth
                label={t('cms.metaDescriptionLabel')}
                value={formData.meta_descricao}
                onChange={handleChange('meta_descricao')}
                margin="normal"
                multiline
                rows={2}
                placeholder={t('cms.metaDescriptionPlaceholder')}
                helperText={t('cms.metaDescriptionCount', { count: formData.meta_descricao?.length || 0 })}
              />

              <TextField
                fullWidth
                label={t('cms.tags')}
                value={formData.tags}
                onChange={handleChange('tags')}
                margin="normal"
                placeholder={t('cms.tagsPlaceholder')}
                helperText={t('cms.tagsHelperText')}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Status */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('cms.statusSection')}
              </Typography>

              <TextField
                fullWidth
                select
                label={t('common.status')}
                value={formData.status}
                onChange={handleSelectChange('status')}
                margin="normal"
              >
                <MenuItem value="rascunho">{t('cms.draft')}</MenuItem>
                <MenuItem value="publicado">{t('cms.published')}</MenuItem>
                <MenuItem value="arquivado">{t('cms.archived')}</MenuItem>
              </TextField>

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.destaque}
                    onChange={handleSwitchChange('destaque')}
                  />
                }
                label={t('cms.featuredArticle')}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>

          {/* Category & Author */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('cms.classification')}
              </Typography>

              <TextField
                fullWidth
                select
                label={t('common.category')}
                value={formData.categoria_id || ''}
                onChange={handleSelectChange('categoria_id')}
                margin="normal"
              >
                <MenuItem value="">{t('cms.noCategory2')}</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.nome}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                select
                label={t('cms.author')}
                value={formData.autor_id || ''}
                onChange={handleSelectChange('autor_id')}
                margin="normal"
              >
                <MenuItem value="">{t('cms.noAuthor2')}</MenuItem>
                {authors.map(author => (
                  <MenuItem key={author.id} value={author.id}>
                    {author.nome}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                fullWidth
                type="number"
                label={t('cms.readTimeMin')}
                value={formData.tempo_leitura}
                onChange={handleSelectChange('tempo_leitura')}
                margin="normal"
                InputProps={{ inputProps: { min: 1 } }}
              />
            </CardContent>
          </Card>

          {/* Featured Image */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('cms.featuredImage')}
              </Typography>

              <TextField
                fullWidth
                label={t('cms.imageUrl')}
                value={formData.imagem_destaque}
                onChange={handleChange('imagem_destaque')}
                margin="normal"
                placeholder="https://..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ImageIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              {formData.imagem_destaque && (
                <Box mt={2}>
                  <img
                    src={formData.imagem_destaque}
                    alt="Preview"
                    style={{
                      width: '100%',
                      height: 150,
                      objectFit: 'cover',
                      borderRadius: 8,
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Publication Date */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('cms.publicationDate')}
              </Typography>

              <TextField
                fullWidth
                type="datetime-local"
                label={t('cms.dateLabel')}
                value={formData.data_publicacao?.slice(0, 16) || ''}
                onChange={handleChange('data_publicacao')}
                margin="normal"
                InputLabelProps={{ shrink: true }}
                helperText={t('cms.emptyForNow')}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PostEditor;
